import React, { useState, useEffect, useMemo } from 'react';
import { Popover, Divider, Flex, Typography, Skeleton, Tabs } from 'antd';
import DailyStatisticsCustomData from 'modules/ReportingModule/pages/InnerReportingPage/pages/dashboard/DailyStatisticsCustomData';
import getFormattedNumber from 'store/redux/utils/getFormattedNumber';
import getFormattedValue from 'store/redux/utils/getFormattedValue';
import ninjaApi from 'store/redux/apis';
import { mdiInformation, mdiChartLine, mdiTableLarge } from '@mdi/js';
import InnerReportingBreadCrumbs from 'modules/ReportingModule/pages/InnerReportingPage/components/InnerReportingBreadCrumbs';
import { NameDefinition } from 'components/NameDefinition';
import AdsetAreaChart from '../AdsetAreaChart';
import { RoasFormatter, RoiFormatter } from 'components/tables/NinjaTable/formatters';
import Icon from '@mdi/react';
import Ninja from '@ninja';
import { useWidgetDate } from 'store/redux/states/dates/dates.hooks';
import DateRangePicker from 'components/DateRangePicker';

const InnerAdset = (props) => {
  const { id, adset } = props;
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const { dates } = useWidgetDate('adsetDailyStatisticsData');

  const [getAdsetTotalsQuery, { data: total, isLoading: loadingTotal }] =
    ninjaApi.useLazyAdsetTotalsQuery();

  const [adsetDailyStatisticsDataQuery, { data: daily, isLoading: loadingDailyData, isFetching }] =
    ninjaApi.useLazyAdsetDailyStatisticsDataQuery();

  useEffect(() => {
    if (open && dates && id) {
      const filters = { ...dates, adset_id: id };
      getAdsetTotalsQuery({ filters });
      adsetDailyStatisticsDataQuery({ filters, sort: [{ columnKey: 'date', direction: 'DESC' }] });
    }
  }, [id, open, dates, getAdsetTotalsQuery, adsetDailyStatisticsDataQuery]);

  const totals = useMemo(() => daily?.totals?.[0] || {}, [daily]);

  const stats = useMemo(
    () => [
      {
        title: 'Ad Clicks',
        name: 'clicks',
        render: (v) => getFormattedNumber(v),
      },
      {
        title: 'CPC',
        name: 'cpc',
        definition: <NameDefinition definition='Cost Per Click' />,
        render: (v) => getFormattedValue(v),
      },
      {
        title: 'CPA',
        name: 'cpa',
        definition: <NameDefinition definition='Cost Per Acquisition' />,
        render: (v) => getFormattedValue(v),
      },
      {
        title: 'Impressions',
        name: 'impressions',
        render: (v) => getFormattedNumber(v),
      },
      {
        title: 'Sessions',
        name: 'sessions',
        render: (v) => getFormattedNumber(v),
      },
      {
        title: 'Deals count',
        name: 'deals_count',
        render: (v) => getFormattedNumber(v),
      },
      {
        title: 'ROI',
        name: 'roi',
        definition: <NameDefinition definition='Return on Investment: Net Profit / Ad Spent' />,
        render: (v) => <RoiFormatter value={v || 0} fontSize={18} />,
      },
      {
        title: 'ROAS',
        name: 'roas',
        definition: <NameDefinition definition='Return on Ad Spent: Gross Income / Ad Spent' />,
        render: (v) => <RoasFormatter value={v} fontSize={18} />,
      },
    ],
    []
  );

  const renderStatCard = (stat) => {
    const statData = total?.[stat.name];

    return (
      <div
        key={stat.name}
        className='border-color-xlight/60 border-solid border-[1px] flex flex-col justify-between w-full h-[75px] rounded-xl p-2'
      >
        <Flex justify='space-between' align='center'>
          <Typography className='font-normal !text-xs text-color-dark-text'>
            {stat.title}
          </Typography>
          {stat.definition && stat.definition}
        </Flex>
        {loadingTotal ? (
          <Skeleton.Input size='small' active />
        ) : (
          <Typography className='!text-lg font-medium'>
            {stat.render ? stat.render(statData) : statData}
          </Typography>
        )}
      </div>
    );
  };

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span className='flex items-center gap-2'>
          <Icon path={mdiChartLine} size={0.7} />
          Overview
        </span>
      ),
      children: (
        <div className='grid gap-3 mt-3 sm:h-full h-[400px] overflow-y-auto'>
          <div className='grid md:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-2.5'>
            {stats.map(renderStatCard)}
          </div>

          <div className='grid sm:grid-cols-2 grid-cols-1 gap-x-6'>
            <AdsetAreaChart
              dataKey='ad_spent'
              data={daily?.data}
              loading={isFetching || loadingDailyData}
              name='Ad Spent'
              color='#eb2f20'
              height={140}
              chartHeight={100}
              total={
                <span className='text-error font-bold'>{getFormattedValue(totals.ad_spent)}</span>
              }
            />

            <AdsetAreaChart
              dataKey='gross_income'
              data={daily?.data}
              loading={isFetching || loadingDailyData}
              name='Gross Income'
              color='#91c716'
              height={140}
              chartType='bar'
              chartHeight={100}
              total={<span className='font-bold'>{getFormattedValue(totals.gross_income)}</span>}
            />

            <AdsetAreaChart
              dataKey='gross_profit'
              data={daily?.data}
              loading={isFetching || loadingDailyData}
              name='Gross Profit'
              color='#ffbb02'
              height={140}
              chartType='bar'
              chartHeight={100}
              total={<span className='font-bold'>{getFormattedValue(totals.gross_profit)}</span>}
            />

            <AdsetAreaChart
              dataKey='net_profit'
              data={daily?.data}
              loading={isFetching || loadingDailyData}
              name='Net Profit'
              color='#536a8b'
              height={140}
              chartHeight={100}
              total={<span className='font-bold'>{getFormattedValue(totals.net_profit)}</span>}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'data',
      label: (
        <span className='flex items-center gap-2'>
          <Icon path={mdiTableLarge} size={0.7} />
          Detailed Data
        </span>
      ),
      children: (
        <div style={{ maxWidth: 800, width: '100%' }}>
          <DailyStatisticsCustomData innerId={id} dates={dates} />
        </div>
      ),
    },
  ];

  const popoverContent = (
    <div className=' w-full sm:w-[800px]'>
      <Flex align='center' gap='small' justify='space-between' wrap>
        <Flex align='center' gap='small'>
          <Typography className='text-sm font-medium text-color-dark-text'>
            {adset?.name}
          </Typography>
          <Popover
            placement='bottom'
            destroyTooltipOnHide
            content={<InnerReportingBreadCrumbs dates={dates} id={id} />}
          >
            <Icon path={mdiInformation} className='cursor-pointer' color={Ninja.colors.darkText} />
          </Popover>
        </Flex>
        <DateRangePicker
          className='w-44'
          date_key='adsetDailyStatisticsData'
          globalDate={false}
          showExpandDays
          formatter='yyyy-MM-dd'
          placement='bottomRight'
          size='middle'
          value={[dates.date_from, dates.date_to]}
          placeholder={['Date from', 'Date to']}
        />
      </Flex>
      <Divider />

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement='right'
      trigger='click'
      content={popoverContent}
    >
      {props.children}
    </Popover>
  );
};

export default InnerAdset;
